import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { API_BASE_URL } from '../utils/env';

export default function EndorsePage() {
  const { token } = useParams();
  const [endorsement, setEndorsement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [endorserName, setEndorserName] = useState('');
  const [endorserTitle, setEndorserTitle] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { decision, message }

  useEffect(() => {
    fetch(`${API_BASE_URL}/endorsements/${token}`)
      .then(r => r.json())
      .then(data => {
        if (data.error) setError(data.error);
        else {
          setEndorsement(data);
          if (data.endorser_name) setEndorserName(data.endorser_name);
        }
      })
      .catch(() => setError('Failed to load endorsement request'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleDecision = async (decision) => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/endorsements/${token}/endorse`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision, endorser_name: endorserName, endorser_title: endorserTitle }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="text-5xl mb-4">🔗</div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Link not found</h1>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  if (result || endorsement?.already_actioned) {
    const isEndorsed = result?.decision === 'endorsed' || endorsement?.status === 'endorsed';
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="text-5xl mb-4">{isEndorsed ? '✅' : '👍'}</div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            {result?.message || (isEndorsed ? 'This achievement has been endorsed' : 'This request has already been actioned')}
          </h1>
          <p className="text-gray-500 text-sm mt-2">You can close this tab.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Verfolio</h1>
          <p className="text-gray-500 text-sm mt-1">Verified Achievement Portfolio</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">
            Endorsement Request from {endorsement.employee_name || endorsement.employee_email}
          </h2>
          <p className="text-sm text-gray-500 mb-6">
            They've logged an achievement and would like you to verify it.
          </p>

          {/* Achievement */}
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 mb-6">
            <div className="flex items-start gap-2 mb-2">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{endorsement.achievement_type}</span>
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">{endorsement.title}</h3>
            {endorsement.description && (
              <p className="text-gray-600 text-sm mb-2 leading-relaxed">{endorsement.description}</p>
            )}
            {endorsement.impact && (
              <p className="text-green-700 text-sm font-medium">Impact: {endorsement.impact}</p>
            )}
          </div>

          {/* Manager details */}
          <div className="space-y-3 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Your name (optional)</label>
              <input
                type="text"
                value={endorserName}
                onChange={e => setEndorserName(e.target.value)}
                placeholder="Jane Smith"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Your title (optional)</label>
              <input
                type="text"
                value={endorserTitle}
                onChange={e => setEndorserTitle(e.target.value)}
                placeholder="Senior Manager, Engineering"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={() => handleDecision('endorsed')}
              disabled={submitting}
              className="flex-1 py-2.5 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              {submitting ? 'Submitting...' : '✓ Endorse this achievement'}
            </button>
            <button
              onClick={() => handleDecision('declined')}
              disabled={submitting}
              className="px-4 py-2.5 text-gray-500 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors text-sm"
            >
              Decline
            </button>
          </div>

          <p className="text-xs text-gray-400 text-center mt-4">
            You don't need a Verfolio account to endorse. This link is unique to you.
          </p>
        </div>
      </div>
    </div>
  );
}
